import {Field, Form, Formik} from 'formik'
import {useState} from 'react'
import * as Yup from 'yup'
import {useIntl} from 'react-intl'
import {useCreateStore} from '../../../context/useMutation'
import {toast} from 'react-toastify'
import {InlineTable} from 'src/feature/bite/component'
import {BreadCrumb} from 'src/_metronic/layout/components'
import {useNavigate} from 'react-router-dom'
import moment from 'moment'
import {ConnectedFocusError} from 'focus-formik-error'

const NewLocationFreelancer = () => {
  const intl = useIntl()
  const {mutateAsync, isLoading} = useCreateStore()
  const navigate = useNavigate()

  const column = [
    [
      {
        title: 'Location Name',
        key: 'name',
        dataIndex: 'name',
        edit: 'input',
        tooltip: 'Provide a name for the location to help differentiate it from others.',
        maxLength: 51,
        required: true,
      },
      {
        title: 'Apt/Building No',
        key: 'aptBuildingNo',
        dataIndex: 'aptBuildingNo',
        edit: 'input',
        tooltip: 'Specify the apartment or building number for accurate location details.',
        maxLength: 51,
        required: true,
      },
      {
        title: 'ZIP Code',
        key: 'zipCode',
        dataIndex: 'zipCode',
        edit: 'input',
        tooltip: 'Enter the postal code associated with this location.',
        maxLength: 21,
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.REGION'}),
        key: 'region',
        dataIndex: 'regionName',
        edit: 'region',
        formName: 'regionId',
        tooltip: 'Specify the region, state, or province where the location is situated.',
      },
      {
        title: 'Latitude',
        key: 'latitude',
        dataIndex: 'latitude',
        edit: 'input',
        maxLength: 51,
        required: true,
        tooltip: 'Enter the geographic latitude of the location for mapping accuracy.',
      },
    ],
    [
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.STATUS'}),
        key: 'status',
        dataIndex: 'status',
        edit: 'select',
        valueSelect: true,
        selects: [
          // {value: 0, text: 'Passive'},
          {value: 1, text: 'Active'},
          {value: 2, text: 'Inactive'},
        ],
        required: true,
        tooltip:
          'Select whether this location is Active or Inactive to manage its visibility on the platform.',
      },
      {
        title: 'Address',
        key: 'addressLine',
        dataIndex: 'addressLine',
        edit: 'input',
        tooltip: 'Enter the full address of the location.',
        maxLength: 101,
        required: true,
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.CITY'}),
        key: 'city',
        dataIndex: 'cityName',
        edit: 'city',
        formName: 'cityID',
        required: true,
        tooltip: 'Enter the city where the location is based.',
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.COUNTRY'}),
        key: 'country',
        dataIndex: 'countryName',
        edit: 'country',
        displayType: 'text',
        formName: 'countryID',
        required: true,
        tooltip: 'Select the country where the location operates.',
      },
      {
        title: 'Longitude',
        key: 'longitude',
        dataIndex: 'longitude',
        edit: 'input',
        tooltip: 'Enter the geographic longitude of the location for mapping accuracy.',
        maxLength: 51,
        required: true,
      },
    ],
  ]

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    aptBuildingNo: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    zipCode: Yup.string().max(
      10,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 10})
    ),
    regionId: Yup.string(),
    longitude: Yup.number()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .typeError(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .min(-180, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: -180}))
      .max(180, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_VALUE'}, {max: 180})),

    status: Yup.number().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    addressLine: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(100, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 100})),

    countryID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    cityID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    latitude: Yup.number()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .typeError(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .min(-90, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: -90}))
      .max(90, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_VALUE'}, {max: 90})),
  })

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          name: '',
          aptBuildingNo: '',
          zipCode: '',
          regionId: '',
          longitude: '',
          status: 1,
          addressLine: '',
          countryID: 'f109e594-b301-ec11-a85d-000d3a229408',
          cityID: '',
          latitude: '',
          agreementDate: moment().utc().format('YYYY-MM-DDTHH:mm:ss'),
          contactCountryPhoneCode: '+971',
        }}
        onSubmit={async (values) => {
          await mutateAsync({values})
        }}
      >
        {({values, errors, touched, resetForm}) => (
          <Form>
            <ConnectedFocusError />
            <div className='d-flex gap-5 align-items-center pb-10'>
              <button
                className='btn btn-primary btn-sm'
                onClick={() => {
                  navigate('/provider')
                }}
              >
                <i className='fa fa-arrow-left'></i>
              </button>
              <BreadCrumb currentPage='Add New Location' />
            </div>
            <div className='card mb-5 mb-xl-10'>
              <div className='card-header'>
                <div className='card-title m-0'>
                  <div>
                    <h3 className='fw-bold mb-2 mt-5'>Location Info</h3>
                    <p className='text-muted fw-normal fs-6'>
                      Enter the details of your location to ensure accurate business operations.
                    </p>
                  </div>
                </div>
              </div>

              <div className='card-body p-9'>
                <InlineTable
                  loading={false}
                  dataSource={values}
                  columns={column}
                  editable={true}
                  values={values}
                />
              </div>
            </div>

            <div className='d-flex justify-content-end mt-10'>
              <button type='submit' className='btn btn-dark ms-2 px-10'>
                Create
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default NewLocationFreelancer
